import { setTaskState, updateTask, assignTask, blockTask, unblockTask, addTaskActivity } from '../db.js';
import { dayKey } from '../tasks.js';
import { patchFor, waitingParty } from '../tasks-v2.js';
import { blockerFrom } from './task-blocker.js';

// THE TASK ACTIONS, v2 (design_handoff_tasks_v2). One implementation of what
// each panel button writes, shared by the Tasks page and the project record's
// Tasks tab so the two cannot drift. The caller supplies how its own screen
// finds a task, applies a change before the write lands, says a write failed,
// and who the team is.
//
//   createTaskActions({ session, find, people, optimistic, fail, acted })
//     -> { tick(id), onAction(id, key, payload), chaseOrNudge(id, key) }
//   onAction throws on a refused write so the panel can say so too.

export function createTaskActions({ session, find, people = () => [], optimistic = () => {}, fail = () => () => {}, acted = {} }) {
  const note = (id, kind, text) => addTaskActivity(id, { kind, text }, session)
    .catch((e) => console.error('[tasks] activity note not saved', e));
  const nameOf = (id) => (people().find((p) => p.userId === id) || {}).name || '';

  async function tick(id) {
    const t = find(id);
    if (!t) return;
    const next = t.state === 'done' ? 'todo' : 'done';
    // Ticking is optimistic; a done row stays, struck through, until the day ends.
    optimistic(id, next === 'done'
      ? { state: 'done', completedAtDay: dayKey(), completedAt: new Date().toISOString() }
      : { state: 'todo', completedAtDay: null, completedAt: null });
    await setTaskState(id, next, session).catch(fail('That change'));
  }

  async function chaseOrNudge(id, key) {
    const t = find(id);
    if (!t) return;
    const patch = patchFor(key, t, dayKey());
    acted[id] = key === 'chase' ? 'Chased' : 'Nudged';
    optimistic(id, patch);
    try {
      await updateTask(id, patch, session);
    } catch (e) {
      delete acted[id];
      fail(key === 'chase' ? 'The chase' : 'The nudge')(e);
      throw e;
    }
    note(id, key === 'chase' ? 'chased' : 'nudged', key === 'chase'
      ? `Chased ${waitingParty(t, people()).who}.` : `Nudged ${nameOf(t.checkerId) || 'the checker'}.`);
  }

  /** The panel's buttons. Throws on a refused write so the panel says so too. */
  async function onAction(id, key, payload = {}) {
    const t = find(id);
    if (!t) throw new Error('That task is no longer in the list.');
    const today = dayKey();
    const write = async (what, fn) => {
      try { await fn(); } catch (e) { fail(what)(e); throw e; }
    };
    switch (key) {
      case 'done':
      case 'approve': {
        optimistic(id, { state: 'done', completedAtDay: today, completedAt: new Date().toISOString() });
        await write('That change', () => setTaskState(id, 'done', session));
        if (key === 'approve') note(id, 'approved', 'Approved.');
        return;
      }
      case 'reopen': {
        optimistic(id, { state: 'todo', completedAtDay: null, completedAt: null });
        await write('That change', () => setTaskState(id, 'todo', session));
        note(id, 'reopened', 'Reopened.');
        return;
      }
      case 'chase':
      case 'nudge':
        return chaseOrNudge(id, key);
      case 'arrived': {
        const back = t.stateBeforeBlock && t.stateBeforeBlock !== 'blocked' ? t.stateBeforeBlock : 'todo';
        optimistic(id, { state: back, blocker: null });
        await write('That change', () => unblockTask(id, back, session));
        note(id, 'unblocked', 'It’s arrived.');
        return;
      }
      case 'takeback': {
        const who = nameOf(t.checkerId);
        const patch = patchFor('takeback', t, today);
        optimistic(id, patch);
        await write('Taking it back', () => updateTask(id, patch, session));
        note(id, 'takeback', who ? `Taken back from ${who}.` : 'Taken back from the checker.');
        return;
      }
      case 'reassign': {
        const person = payload.person || null;
        optimistic(id, { assigneeId: person ? person.userId : null, assigneeEmail: person ? person.email : null });
        await write('The reassignment', () => assignTask(id, person, session));
        note(id, 'assigned', person ? `Moved to ${person.name}.` : 'Unassigned.');
        return;
      }
      case 'check': {
        const person = payload.person;
        if (!person) { const err = new Error('Pick who checks it.'); err.plain = true; throw err; }
        const patch = patchFor('check', t, today, { checkerId: person.userId });
        optimistic(id, patch);
        await write('Sending it for check', () => updateTask(id, patch, session));
        note(id, 'review', `Sent to ${person.name} for check.`);
        return;
      }
      case 'block': {
        let blocker;
        try { blocker = blockerFrom(payload.draft || {}, people(), session, today); } catch (e) { e.plain = true; throw e; }
        const from = t.state;
        optimistic(id, { state: 'blocked', blocker, stateBeforeBlock: from });
        await write('Marking it as waiting', () => blockTask(id, blocker, session, from));
        // The note appears verbatim in the trail, in the person's words.
        note(id, 'blocked', blocker.note || `Waiting on ${blocker.waitingOnName}.`);
        return;
      }
      case 'return': {
        const text = String(payload.text || '').trim();
        if (!text) { const err = new Error('Say what needs changing.'); err.plain = true; throw err; }
        const patch = patchFor('return', t, today);
        optimistic(id, patch);
        await write('Returning it', () => updateTask(id, patch, session));
        note(id, 'comment', `Returned with comments: ${text}`);
        return;
      }
      default:
        throw new Error(`Unknown action ${key}`);
    }
  }

  return { tick, onAction, chaseOrNudge };
}
